import { useQuery } from "@tanstack/react-query";
import { Truck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getLocalTodayYMD, formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";

interface TickerLoad {
  pickup_date: string | null;
  origin_city: string | null;
  origin_state: string | null;
  destination_city: string | null;
  destination_state: string | null;
  equipment_type: string | null;
  weight_lbs: number | null;
}

const FALLBACK = "Active freight in all 48 states · Van · Reefer · Flatbed · Brokers & Carriers";

const prettyEquipment = (eq: string | null): string => {
  if (!eq) return "";
  return eq
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const formatDate = (ymd: string | null): string => {
  if (!ymd) return "";
  return formatYYYYMMDDToLocaleDateString(ymd, undefined, { month: "short", day: "numeric" });
};

const formatWeight = (w: number | null): string => {
  if (!w || w <= 0) return "";
  return `${Math.round(w / 1000)}K lbs`;
};

const fetchLiveLoads = async (): Promise<TickerLoad[]> => {
  const today = getLocalTodayYMD();
  const { data, error } = await supabase
    .from("loads")
    .select("pickup_date, origin_city, origin_state, destination_city, destination_state, equipment_type, weight_lbs")
    .eq("is_public", true)
    .eq("status", "posted")
    .gte("pickup_date", today)
    .order("created_at", { ascending: false })
    .limit(40);

  if (error) throw error;
  return (data ?? []).filter(
    (l) => l.origin_city && l.origin_state && l.destination_city && l.destination_state,
  );
};

const Pill = ({ load }: { load: TickerLoad }) => {
  const date = formatDate(load.pickup_date);
  const equipment = prettyEquipment(load.equipment_type);
  const weight = formatWeight(load.weight_lbs);

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-xs sm:text-sm whitespace-nowrap shadow-sm">
      <Truck className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      {date && <span className="font-normal text-gray-500">{date}</span>}
      {date && <span className="text-gray-300">·</span>}
      <span className="font-medium text-gray-800">
        {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
      </span>
      {equipment && (
        <>
          <span className="text-gray-300">·</span>
          <span className="inline-flex items-center gap-1 text-[#02857E] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#02857E]" />
            {equipment}
          </span>
        </>
      )}
      {weight && (
        <>
          <span className="text-gray-300">·</span>
          <span className="font-normal text-gray-500">{weight}</span>
        </>
      )}
    </div>
  );
};

export const LiveLoadsTicker = () => {
  const { data: loads, isLoading } = useQuery({
    queryKey: ["landing-live-loads"],
    queryFn: fetchLiveLoads,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading || !loads || loads.length === 0) {
    return (
      <div className="px-4 text-center">
        <div className="text-xs sm:text-sm text-gray-500 leading-relaxed max-w-3xl mx-auto">
          {FALLBACK}
        </div>
      </div>
    );
  }

  // Duplicate for seamless loop
  const doubled = [...loads, ...loads];

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 px-1 pb-2 mb-3 border-b border-gray-200">
        <span
          className="inline-block w-2 h-2 rounded-full bg-[#02857E]"
          style={{ animation: "live-loads-pulse 2s ease-in-out infinite" }}
        />
        <span className="text-xs font-semibold text-gray-700 tracking-wide">
          Latest Posted Loads
        </span>
      </div>
      <div
        className="relative w-full overflow-hidden"
        style={{
          maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
        }}
      >
        <style>{`
          @keyframes live-loads-scroll {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          @keyframes live-loads-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
          .live-loads-track {
            animation: live-loads-scroll 90s linear infinite;
          }
          .live-loads-track:hover {
            animation-play-state: paused;
          }
          @media (prefers-reduced-motion: reduce) {
            .live-loads-track { animation: none; }
          }
        `}</style>
        <div className="live-loads-track flex w-max gap-3 sm:gap-4">
          {doubled.map((load, i) => (
            <Pill key={i} load={load} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveLoadsTicker;
